import Avatar, { IAvatar } from "../Avatar";
import { userTypeConvert } from "@/utils/helper";
import Link from "next/link";
import { PAGE_URL } from "@/utils/constants";
import { motion } from "framer-motion";
import { capitalize } from "lodash";
import { CSSProperties, memo, useEffect, useMemo, useState } from "react";
import ModalMutualConnection from "../pages/connection/ModalMutualConnection";
import useOrgSettingStore from "@/store/useOrgSettingsStore";
import { useGetCGPercents } from "@/hooks/useUsers";
import { useGetCGPercentContextWait } from "@/hooks/useCircles";
import { useRouter } from "next/router";
import useAuthStore from "@/store/useAuthStore";
import useWindowResize from "@/hooks/useWindowResize";
import cx from "classnames";
import { useAnalytics } from "@/hooks/useAnalytics";
import useVisitOtherProfileStore from "@/hooks/useVisitOtherProfileStore";
import { LoadingOutlined } from "@ant-design/icons";
import { IoAdd } from "react-icons/io5";

interface ICardUserProfile {
  staticImg?: boolean;
  type?: "profile" | "circle" | "event";
  styleTier?: CSSProperties;
  reference?: string;
  findCGCTX?: "EVENT" | "CIRCLE";
  cgPercent?: number | null;
  mutualCount?: number;
  hideJobType?: boolean;
  hideCG?: boolean;
  item: {
    isClosedForAcceptingMentee: boolean;
    id: string;
    cognitoID: string;
    type_tier: IAvatar["type_tier"];
    email: string;
    organizationGroup: string;
    firstName: string;
    lastName: string;
    username: string;
    avatarUrl: string;
    bannerImageUrl: string;
    bio: string | null;
    jobTitle: string;
    type: string;
    lookingForType: string;
    joinedAt: string;
    phone: string;
    points: number;
    lifetimePoints: number | null;
    dateOfBirth: string;
    focusedSkillTrack: any | null; // You can replace "any" with an appropriate type
    minimumIntroducers: number;
    interest: string[]; // You can replace "string" with a more specific type if needed
    expertises: string[]; // You can replace "string" with a more specific type if needed
    department: {
      id: string;
      name: string;
      description: string | null;
      organizationGroup: string;
      skillTracks: {
        nextToken: string | null;
        startedAt: string | null;
        __typename: string;
      };
      createdAt: string;
      updatedAt: string;
      _version: number;
      _deleted: boolean | null;
      _lastChangedAt: number;
      __typename: string;
    };
    almaMater: string;
    titleRank: string;
    awards: string[]; // You can replace "string" with a more specific type if needed
    gender: string;
    race: string;
    ethnicity: string;
    sexuality: string;
    vegetarian: boolean;
    vegan: boolean;
    personalityType: string;
    religion: string;
    class: string;
    relationshipStatus: string;
    taxBracket: string;
    birthCountry: string;
    birthCity: string;
    childhoodZip: number;
    currentZip: number;
    dogOwner: boolean;
    catOwner: boolean;
    moonSign: string;
    sunSign: string;
    artist: string[]; // You can replace "string" with a more specific type if needed
    musicalInstrument: string[]; // You can replace "string" with a more specific type if needed
    morningVsNightPerson: string;
    generation: string;
    hasSiblings: boolean;
    totalSiblings: number;
    birthOrder: number;
    favorites: {
      faveMusicGenre: string;
      faveSinger: string;
      faveMuseum: string;
      faveSportTeam: string;
      faveSport: string;
      faveTVShow: string;
      faveBoardGame: string;
      faveVideoGame: string;
      faveBook: string;
      faveTheatreShow: string;
      faveVacation: string;
      faveNewsSource: string;
      faveCity: string;
      faveRestaurant: string;
      faveBar: string;
      faveNightClub: string;
      favePoliticalParty: string;
      faveCharities: string[]; // You can replace "string" with a more specific type if needed
      __typename: string;
    };
    champions: {
      items: any[]; // You can replace "any" with an appropriate type
      nextToken: string | null;
      startedAt: string | null;
      __typename: string;
    };
    heroes: string[]; // You can replace "string" with a more specific type if needed
    sages: string[]; // You can replace "string" with a more specific type if needed
    celebsLoveToMeet: string[]; // You can replace "string" with a more specific type if needed
    introVsExtro: string;
    eatOutVsEatIn: string;
    createdAt: string;
    updatedAt: string;
    _version: number;
    _deleted: boolean | null;
    _lastChangedAt: number;
    userFocusedSkillTrackId: string | null;
    userDepartmentId: string;
    __typename: string;
  };
  variant?: "vertical" | "horizontal";
  className?: string;
  cardClassName?: string;
  hideType?: boolean;
  customTypeText?: boolean;
  isChampion?: boolean;
  disableHref?: boolean;
  disableAvatarOnlyHref?: boolean;
  hideMutual?: boolean;
  points?: number;
  avatarSize?: "xxs" | "xs" | "sm" | "sm" | "md" | "lg" | "xl" | "2xl";
  customType?: string | any;
  additionalType?: string;
  squareAvatar?: boolean;
  findCG?: boolean;
  avatarOnly?: boolean;
  hideOnlyType?: boolean;
  isCapitalizeOnName?: boolean;
  hrefQueryUrl?: string;
  isOrganizer?: boolean;
  avatarAuto?: boolean;
  quickConnect?: boolean;
  onQuickConnect?: (id: string, callback?: () => void) => void;
}

const hrefLink: Record<string, string> = {
  profile: PAGE_URL.profile,
  circle: PAGE_URL.circles,
  event: PAGE_URL.events,
};

export const useTypeTier = (points: number) => {
  const { pointTiers } = useOrgSettingStore();

  return useMemo(() => {
    if (points === null) {
      return "";
    }

    if (pointTiers?.platinum != null && points >= pointTiers.platinum)
      return "PLATINUM";
    if (pointTiers?.gold != null && points >= pointTiers.gold) return "GOLD";
    if (pointTiers?.silver != null && points >= pointTiers.silver)
      return "SILVER";
    if (pointTiers?.bronze != null && points >= pointTiers.bronze)
      return "BRONZE";

    return "";
  }, [pointTiers, points]);
};

export const useTypeTierFunc = () => {
  const { pointTiers } = useOrgSettingStore();

  const typeTierConverter = (points: number) => {
    if (points === null) {
      return;
    }

    if (pointTiers?.platinum != null && points >= pointTiers.platinum)
      return "PLATINUM";
    if (pointTiers?.gold != null && points >= pointTiers.gold) return "GOLD";
    if (pointTiers?.silver != null && points >= pointTiers.silver)
      return "SILVER";
    if (pointTiers?.bronze != null && points >= pointTiers.bronze)
      return "BRONZE";
  };

  return { typeTierConverter };
};

function CardUserProfile({
  type = "profile",
  item,
  cgPercent = null,
  mutualCount,
  variant = "vertical",
  className,
  cardClassName,
  hideType,
  customTypeText,
  isChampion,
  disableHref,
  disableAvatarOnlyHref = true,
  hideMutual,
  points,
  avatarSize,
  customType,
  squareAvatar,
  findCG,
  hideJobType,
  avatarOnly,
  findCGCTX,
  reference,
  styleTier,
  hideOnlyType,
  hideCG,
  isCapitalizeOnName = false,
  hrefQueryUrl = "",
  additionalType,
  staticImg,
  isOrganizer,
  avatarAuto,
  quickConnect,
  onQuickConnect = (id: string, callback?: () => void) => {},
}: ICardUserProfile) {
  const router = useRouter();
  const { user } = useAuthStore();
  const { pointTiers } = useOrgSettingStore();

  const LinkMotion = motion(Link);
  const [showMutualConnection, setShowMutualConnection] = useState<{
    isOpen: boolean;
    userId: any;
  }>({
    isOpen: false,
    userId: null,
  });
  const [isLoadingQuickConnect, setIsLoadingQuickConnect] =
    useState<boolean>(false);

  const { data: cgGetSingle } = useGetCGPercents(
    [item?.id],
    1,
    reference,
    !!findCG
  );

  const { data: cgGetSingleContext, refetch: refetchCGPContext } =
    useGetCGPercentContextWait(
      {
        referenceIds: [item?.id],
        limit: 1,
        type: findCGCTX,
      },
      reference
    );

  const CGP = useMemo(() => {
    if (cgPercent !== null) {
      return cgPercent;
    }

    const getCgPercent = (cgData: any) => cgData?.[0]?.CGPercent;

    return (
      getCgPercent(cgGetSingle) || getCgPercent(cgGetSingleContext) || null
    );
  }, [cgGetSingle, cgGetSingleContext, cgPercent]);

  const isHRView = useMemo(
    () => router.query?.view === "admin",
    [router?.query]
  );

  const isClosedForAcceptingMentee = useMemo(() => {
    return item?.isClosedForAcceptingMentee === true;
  }, [item?.isClosedForAcceptingMentee]);

  const typeTier:
    | "PLATINUM"
    | "GOLD"
    | "SILVER"
    | "BRONZE"
    | "ORIGINAL"
    | "DIAMOND"
    | undefined = useMemo(() => {
    if (pointTiers?.platinum && points && points >= pointTiers.platinum)
      return "PLATINUM";
    if (pointTiers?.gold && points && points >= pointTiers.gold) return "GOLD";
    if (pointTiers?.silver && points && points >= pointTiers.silver)
      return "SILVER";
    if (
      pointTiers?.bronze != null &&
      points != null &&
      points >= pointTiers.bronze
    )
      return "BRONZE";
    return undefined;
  }, [pointTiers, points]);

  const { track } = useAnalytics();

  const handleClick = () => {
    const from = window.location.pathname;
    const to = `${hrefLink[type]}/${item?.id}${hrefQueryUrl ? `?${hrefQueryUrl}` : ""}`;

    track("Navigation", `${type} Card Click`, `From: ${from} To: ${to}`);
  };

  const Wrapper = ({
    children,
    disableHref,
  }: {
    children: any;
    disableHref?: boolean;
  }) =>
    disableHref ? (
      <motion.div className={className}>{children}</motion.div>
    ) : (
      <LinkMotion
        href={`${hrefLink[type]}/${item?.id}${hrefQueryUrl ? `?${hrefQueryUrl}` : ""}`}
        whileHover={{ scale: 0.99 }}
        className={className}
        onClick={handleClick}
      >
        {children}
      </LinkMotion>
    );

  useEffect(() => {
    if (findCGCTX && item && !cgPercent) {
      refetchCGPContext();
    }
  }, [findCGCTX, item, cgPercent, refetchCGPContext]);

  return (
    <>
      {avatarOnly && (
        <Wrapper disableHref={disableAvatarOnlyHref}>
          <Avatar
            staticImg={staticImg}
            src={item?.avatarUrl}
            alt="avatar"
            badge={
              (isHRView && user?.bio?.userId === item?.id) || hideCG || isHRView
                ? null
                : CGP
            }
            type_tier={typeTier}
            size={avatarSize || "lg"}
            isRounded={!squareAvatar}
            avatarAuto={avatarAuto}
            id={item?.id}
            name={item?.firstName}
          />
        </Wrapper>
      )}

      {!avatarOnly && (
        <Wrapper disableHref={disableHref}>
          {variant === "vertical" && (
            <div
              className={cx(
                "flex flex-col items-center bg-card pb-4 relative pt-16 px-4 lg:px-0 h-full",
                cardClassName
              )}
            >
              <div className="absolute -top-12">
                <Avatar
                  staticImg={staticImg}
                  src={item?.avatarUrl}
                  alt="avatar"
                  badge={
                    (isHRView && user?.bio?.userId === item?.id) ||
                    hideCG ||
                    isHRView
                      ? null
                      : CGP
                  }
                  type_tier={typeTier}
                  size={avatarSize || "lg"}
                  isRounded={!squareAvatar}
                  styleTier={styleTier}
                  avatarAuto={avatarAuto}
                  id={item?.id}
                  name={item?.firstName}
                />
              </div>
              {quickConnect && (
                <div
                  className="text-yellow cursor-pointer -top-[50px] right-0 absolute"
                  onClick={(evt) => {
                    evt.preventDefault();
                    evt.stopPropagation();
                    setIsLoadingQuickConnect(true);
                    onQuickConnect?.(item?.id, () => {
                      setIsLoadingQuickConnect(false);
                    });
                  }}
                >
                  {isLoadingQuickConnect ? (
                    <LoadingOutlined />
                  ) : (
                    <IoAdd size={24} />
                  )}
                </div>
              )}
              <div className="flex flex-col justify-between h-full pt-4 w-full lg:px-4">
                <div className="text-center">
                  {!hideJobType && (
                    <p className="text-[10px] text-[#D9D9D9]">
                      {item?.jobTitle}
                    </p>
                  )}
                  <p
                    className={`text-sm font-bold ${isChampion && "text-yellow"} truncate break-words`}
                  >
                    {isCapitalizeOnName
                      ? `${item?.firstName ? capitalize(item?.firstName) : ""} ${
                          item?.lastName ? capitalize(item?.lastName) : ""
                        }`
                      : `${item?.firstName ? item?.firstName : ""} ${
                          item?.lastName ? item?.lastName : ""
                        }`}
                  </p>

                  <div className="flex flex-col">
                    <div className="flex justify-center gap-[2px] lg:gap-2">
                      <div className="min-w-[8px] w-2 mt-1 lg:mt-0 h-2 lg:w-3 lg:h-3 bg-[#10C700] rounded-full" />
                      <p
                        className={cx(
                          "lg:text-[12px] text-[#D9D9D9] text-[12px]"
                        )}
                      >
                        {!hideType &&
                          (item?.type === "ALLY" || !item?.type
                            ? ""
                            : `${userTypeConvert(item?.type as never)} `)}
                        Actively Looking for{" "}
                        {item?.lookingForType
                          ? userTypeConvert(
                              item?.lookingForType as never,
                              undefined,
                              true
                            )
                          : "Connections"}
                        {customTypeText && customTypeText}
                      </p>
                    </div>
                    {isClosedForAcceptingMentee && (
                      <div className="flex justify-center gap-[2px] lg:gap-2">
                        <div className="mt-1 min-w-2 w-2 lg:mt-0 h-2 lg:w-3 lg:h-3 bg-[#FF7875] rounded-full hexagon" />
                        <p className="text-[12px] lg:text-[10px] text-neutral-300">
                          No Capacity for New Mentees
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {!hideMutual && (
                  <>
                    <div className="my-[6px] w-full h-[1px] border-b border-b-[#D9D9D9]" />

                    <div className="flex flex-col items-center gap-[6px]">
                      <p className="text-[10px] text-[#D9D9D9]">
                        Mutual Connections
                      </p>
                      <p
                        className="bg-[#DAFFD6] text-[#158284] w-6 h-6 rounded-full flex items-center justify-center"
                        onClick={() =>
                          setShowMutualConnection({
                            isOpen: true,
                            userId: item?.id,
                          })
                        }
                      >
                        {mutualCount}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {variant === "horizontal" && (
            <div className="flex gap-4 items-center">
              <div>
                <Avatar
                  staticImg={staticImg}
                  src={item?.avatarUrl}
                  alt="avatar"
                  badge={
                    (isHRView && user?.bio?.userId === item?.id) ||
                    hideCG ||
                    isHRView
                      ? null
                      : user?.bio?.userId === item?.id
                        ? 100
                        : CGP
                  }
                  type_tier={typeTier}
                  badgeXPos={squareAvatar ? "right" : "center"}
                  size={avatarSize || "lg"}
                  isRounded={!squareAvatar}
                  styleTier={styleTier}
                  avatarAuto={avatarAuto}
                  id={item?.id}
                  name={user?.bio?.firstName}
                />
              </div>
              <div className="inline-grid">
                {!hideJobType && (
                  <p className="text-[12px] text-[#D9D9D9]">{item?.jobTitle}</p>
                )}
                <p
                  className={`text-sm font-bold ${isChampion && "text-yellow"} truncate break-words`}
                >
                  {isCapitalizeOnName
                    ? `${item?.firstName ? capitalize(item?.firstName) : ""} ${
                        item?.lastName ? capitalize(item?.lastName) : ""
                      }`
                    : `${item?.firstName ? item?.firstName : ""} ${
                        item?.lastName ? item?.lastName : ""
                      }`}
                </p>
                {customType ? (
                  typeof customType === "string" ? (
                    <p className="text-[12px] text-[#D9D9D9]">{customType}</p>
                  ) : (
                    customType
                  )
                ) : (
                  <p
                    className={cx("lg:text-[12px] text-[#D9D9D9] text-[12px]")}
                  >
                    {!hideType && (
                      <>
                        {!hideOnlyType &&
                          (item?.type === "ALLY" || !item?.type
                            ? ""
                            : `${userTypeConvert(item?.type as never)} `)}
                        Actively Looking for{" "}
                        {item?.lookingForType
                          ? userTypeConvert(
                              item?.lookingForType as never,
                              undefined,
                              true
                            )
                          : "Connections"}
                      </>
                    )}
                    {customTypeText && customTypeText}
                  </p>
                )}
                {additionalType && (
                  <p className="text-[12px] text-[#D9D9D9]">{additionalType}</p>
                )}
                {isOrganizer && (
                  <div className="bg-[#DAFFD6] py-[2px] px-2 mt-1 w-fit">
                    <p className="text-xs text-[#158284]">Organizer</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </Wrapper>
      )}

      {showMutualConnection?.isOpen && (
        <ModalMutualConnection
          idUser={showMutualConnection?.userId}
          isOpen={showMutualConnection?.isOpen}
          onClose={() =>
            setShowMutualConnection({
              isOpen: false,
              userId: null,
            })
          }
        />
      )}
    </>
  );
}

export default memo(CardUserProfile);
